import axios, { AxiosResponse } from "axios";
import { NavigationRoute, Employee } from "../types";

const { REACT_APP_GETUSER_URL, REACT_APP_APPCUES_ENABLED_URL } = process.env;
const APP_CONTAINER = document.getElementById("SS-DashboardWrapper");

// axios.defaults.baseURL = 'http://localhost:3000';
// Existing window object is located in src/index.tsx

// Setup antiforgery token interceptors
declare global {
    interface Window {
        AntiForgeryToken?: string;
        _antiTokenRefreshTimer?: ReturnType<typeof setInterval>;
        axios?: typeof axios & { _antiForgeryPatched?: boolean };
        fetch: typeof fetch & { _antiForgeryPatched?: boolean };
        XMLHttpRequest: {
            new(): XMLHttpRequest;
            prototype: XMLHttpRequest;
            _antiForgeryPatched?: boolean;
        } & typeof XMLHttpRequest;
    }
}
interface XMLHttpRequestWithTracking extends XMLHttpRequest {
    _method?: string;
    _url?: string;
    _headers?: Record<string, string>;
}
(function(): void {
    const appName: string = "DashboardWrapper";
    // CRITICAL: Capture original functions ONCE at module load, not in setupInterceptors
    const originalFetch: typeof fetch = window.fetch;
    const originalXHR: typeof XMLHttpRequest = window.XMLHttpRequest;
    // Helper function to check if URL should get anti-forgery token
    const shouldAddToken = function(url: string | URL | RequestInfo): boolean {
        if (!url) return true; // Default to true for relative URLs
        const urlString = url.toString();
        if (urlString.indexOf('://') === -1) return true; // Relative URL
        try {
            const urlObj = new URL(urlString);
            const hostname = urlObj.hostname.toLowerCase();
            // Allow localhost (any port) for development
            if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
            // Allow current domain
            if (hostname === window.location.hostname.toLowerCase()) return true;
            // Allow affinitylogon.com and subdomains
            if (hostname === 'affinitylogon.com' || hostname.endsWith('.affinitylogon.com')) return true;
            // Allow testaffinitylogon.com and subdomains  
            if (hostname === 'testaffinitylogon.com' || hostname.endsWith('.testaffinitylogon.com')) return true;
            return false; // External domain - don't add token
        } catch (e) {
            return true; // If URL parsing fails, default to adding token
        }
    };
    const setupInterceptors = function(): void {
        // Add XMLHttpRequest interceptor that adds antiforgery token for non-GET requests
        if (window.XMLHttpRequest && !(window.XMLHttpRequest as any)._antiForgeryPatched)
        {
          (window.XMLHttpRequest as any)._antiForgeryPatched = true;
          // originalXHR already captured at module load time
          (window.XMLHttpRequest as any) = function (): XMLHttpRequest
          {
            const xhr = new originalXHR!() as XMLHttpRequestWithTracking;
            const originalOpen = xhr.open;
            const originalSend = xhr.send;
            let method: string = '';
            let url: string = '';
            xhr.open = function (
              httpMethod: string,
              httpUrl: string | URL,
              async: boolean = true,
              user?: string | null,
              password?: string | null
            ): void
            {
              method = httpMethod ? httpMethod.toUpperCase() : 'GET';
              url = httpUrl.toString() || '';
              return originalOpen.call(this, httpMethod, httpUrl, async, user, password);
            };
            xhr.send = function (body?: Document | XMLHttpRequestBodyInit | null): void
            {
              const antiForgeryToken: string = (window as any).AntiForgeryToken || "";
              if (antiForgeryToken && method !== 'GET' && shouldAddToken(url))
              {
                xhr.setRequestHeader('__RequestVerificationToken', antiForgeryToken);
                console.log(`${appName}: Added antiforgery token to XMLHttpRequest`, method, url);
              }
              else if (method !== 'GET' && !shouldAddToken(url))
              {
                console.log(`${appName}: Skipping antiforgery token for external XMLHttpRequest`, method, url);
              }
              else if (method !== 'GET')
              {
                console.log(`${appName}: No antiforgery token available for XMLHttpRequest`, method, url);
              }
              return originalSend.call(this, body);
            };
            return xhr;
          };
          for (const prop in originalXHR)
          {
            if (Object.prototype.hasOwnProperty.call(originalXHR, prop))
            {
              (window.XMLHttpRequest as any)[prop] = (originalXHR as any)[prop];
            }
          }
          window.XMLHttpRequest.prototype = originalXHR!.prototype;
          console.log(`%c${appName}: XMLHttpRequest injected anti-forgery token interceptor`, "color: green");
        }
        else if ((window.XMLHttpRequest as any)?._antiForgeryPatched)
        {
          console.log(`%c${appName}: XMLHttpRequest already injected anti-forgery token interceptor`, "color: yellow");
        }
        // Add Fetch interceptor that adds antiforgery token for non-GET requests
        if (window.fetch && !window.fetch._antiForgeryPatched) {
            window.fetch._antiForgeryPatched = true;
            // originalFetch already captured at module load time
            window.fetch = function(url: RequestInfo | URL, options: RequestInit = {}): Promise<Response> {
                options.headers = options.headers || {};
                (options.headers as Record<string, string>)['X-Requested-With'] = 'XMLHttpRequest';
                options.credentials = options.credentials || 'include';
                const method: string = (options.method || 'GET').toUpperCase();
                const antiForgeryToken: string = window.AntiForgeryToken || "";
                if (antiForgeryToken && method !== 'GET' && shouldAddToken(url)) {
                    delete (options.headers as Record<string, string>)['__RequestVerificationToken'];
                    (options.headers as Record<string, string>)['__RequestVerificationToken'] = antiForgeryToken;
                    console.log(appName + ": Added antiforgery token to fetch", method.toUpperCase(), url);
                } else if (method !== 'GET' && !shouldAddToken(url)) {
                    console.log(appName + ": Skipping antiforgery token for external fetch", method.toUpperCase(), url);
                } else if (method !== 'GET') {
                    console.log(appName + ": No antiforgery token available for fetch", method.toUpperCase(), url);
                }
                return originalFetch!.call(this, url, options).then(function(response: Response) {
                    // Add any 401 response handlers here
                    return response;
                }).catch(function(error: any) {
                    // Add any 401 response handlers here
                    throw error;
                });
            };
            console.log("%c" + appName + ": Fetch injected anti-forgery token interceptor", "color: green");
        }
        else if (window.fetch && window.fetch._antiForgeryPatched)
        {
            console.log("%c" + appName + ": Fetch already injected anti-forgery token interceptor", "color: yellow");
        }
    };
    // Refresh antiforgery token
    const refreshAntiForgeryToken = function(callbackMethod?: (error?: Error, token?: string) => void): Promise<string> {
        const callback = typeof callbackMethod === 'function' ? callbackMethod : function () {};
        const xhr = new XMLHttpRequest();
        return new Promise<string>(function(resolve, reject) {
            xhr.open('GET', '/Api/RefreshAntiForgeryToken', true);
            xhr.onreadystatechange = function() {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        try {
                            const data = JSON.parse(xhr.responseText);
                            if (data && data.token) {
                                window.AntiForgeryToken = data.token;
                                console.log(appName + ": Antiforgery token refreshed successfully");
                                setupInterceptors();
                                // Token obtained successfully
                                resolve(data.token);
                                callback(undefined, data.token);
                            } else {
                                const error = new Error(appName + ": Invalid antiforgery token refresh response");
                                reject(error);
                                callback(error);
                            }
                        } catch (e) {
                            reject(e);
                            callback(e as Error);
                        }
                    } else {
                        const error = new Error(appName + ": Failed to refresh antiforgery token, status: " + xhr.status);
                        reject(error);
                        callback(error);
                    }
                }
            };
            xhr.send();
        });
    };
    // Token refresh no longer depends on login events - using daily seed approach
    if (window.AntiForgeryToken) {
        console.log(appName + ": Initial antiforgery token set.");
        setupInterceptors();
        clearInterval(window._antiTokenRefreshTimer);
        window._antiTokenRefreshTimer = setInterval(function() {
            refreshAntiForgeryToken();
        }, 2 * 60 * 60 * 1000);
    } else {
        console.log(appName + ": Initial antiforgery token is not set. Attempt to get one..");
        refreshAntiForgeryToken()
            .then(function(token: string) {
                clearInterval(window._antiTokenRefreshTimer);
                window._antiTokenRefreshTimer = setInterval(function() {
                    refreshAntiForgeryToken();
                }, 2 * 60 * 60 * 1000);
            })
            .catch(function(error: Error) {
                console.error(appName + ": Failed to get initial antiforgery token:", error);
                clearInterval(window._antiTokenRefreshTimer);
                window._antiTokenRefreshTimer = setInterval(function() {
                    refreshAntiForgeryToken();
                }, 2 * 60 * 60 * 1000);
            });
    }
    // Logout handling no longer needed - tokens are session-independent
})();
// End: Setup antiforgery token interceptors

interface IInitDataProps {
    user: Employee;
    navLinks?: NavigationRoute[];
    formLinks?: NavigationRoute;
    activePage?: string;
}

export function fetchInitialData() {
    let initData = getInitialData();
    return {
        payslip: wrapPromise(initData),
    };
}

export const getInitialData = async (): Promise<IInitDataProps> => {
    const navConfig = await getNavConfig();
    const { links, formLinks, activePage } = processUserNavLinks(navConfig);
    const user = await getUserInfo();

    user.EmployeeNo = user.EmployeeNumber || user.EmployeeNo;
    user.CompanyNo = user.CompanyNumber || user.CompanyNo;

    // Only SSL has the Firstname & Lastname field
    user.FirstName = user.FirstName || user.Firstname || user.firstName;
    user.LastName = user.LastName || user.Lastname || user.lastName;

    if (window.myVue !== undefined) {
        if (window.myVue.config?.globalProperties) {
            window.myVue.config.globalProperties.$config.value = navConfig.data;
            window.myVue.config.globalProperties.$user.value = user;
            if (!window.Affinity) {
                window.Affinity = {
                    user: user,
                    config: navConfig.data,
                    companyNumber: user.CompanyNo || user.companyNo,
                    employeeNumber: user.EmployeeNo || user.employeeNo,
                    EmployeeNo: user.EmployeeNo || user.employeeNo,
                    CompanyNo: user.CompanyNo || user.companyNo,
                };
            } else {
                window.Affinity.user = user;
                window.Affinity.config = navConfig.data;
                window.Affinity.companyNumber = user.CompanyNo;
                window.Affinity.employeeNumber = user.EmployeeNo;
                window.Affinity.user.CompanyNo = user.CompanyNo;
                window.Affinity.user.EmployeeNo = user.EmployeeNo;
            }
        } else {
            window.myVue.$config = navConfig.data;
            window.myVue.$user = user;
        }
    }

    return {
        user,
        navLinks: links,
        formLinks,
        activePage,
    };
};

export const getUserInfo = () => {
    const userInfoApi = APP_CONTAINER?.dataset.user || "/Config/GetInfo";
    const apiMethod = APP_CONTAINER?.dataset.userMethod || "post";
    if (apiMethod === "get") {
        return axios.get(`${userInfoApi}`).then((res) => res.data);
    } else {
        return axios.post(`${userInfoApi}`).then((res) => res.data);
    }
};

export const getAppcuesUserIdentity = () => {
    return axios.get(`${REACT_APP_GETUSER_URL}`).then((res) => res.data);
};

export const getAnnouncement = async () => {
    const api = APP_CONTAINER?.dataset.announcement;
    const apiMethod = APP_CONTAINER?.dataset.announcementMethod || "get";
    if (apiMethod === "get") {
        return axios.get(`${api}`).then((res) => res.data);
        // .catch((err) => console.log(err.data));
    } else {
        return axios.post(`${api}`).then((res) => res.data);
    }
};

export const getAppcuesEnabled = async () => {
    return axios.get(`${REACT_APP_APPCUES_ENABLED_URL}`).then((res) => res.data);
};

function processUserNavLinks(res: AxiosResponse<any, any>): {
    links: NavigationRoute[];
    formLinks: NavigationRoute;
    activePage: string;
} {
    const config: any = res.data;
    let activePage = "",
        hasActive = false;

    if (window.Affinity.app === "Dashboard") {
        activePage = "Dashboard";
    }

    const links: NavigationRoute[] = [
        {
            link: process.env.REACT_APP_DASHBOARD_URL as string,
            label: "Dashboard",
            icon: "dashboard",
            id: "Dashboard",
            enabled: true,
        },
    ];

    let formLinks: any = config.Forms.filter((m: any) => m.Url).map((m: any) => ({
        ...assignLinkValues(m),
    }));
    const _myProfileSubLinks: any = [];

    // I was against this but this needs to properly address in the future
    // Insert My Details in My Profile
    const myDetailsIndx = formLinks.findIndex((a: any) => a.id.includes("MyDetails") || a.id.includes("My Details")),
        myDetails = formLinks[myDetailsIndx];

    (config.Apps || []).forEach((app: any) => {
        if (app.Link) {
            const { Menu, Name } = app.Link;
            let isCurrentPage = false,
                myDetailsIsInserted = false;
            let subLinks: NavigationRoute[] = [];
            const linkValues = assignLinkValues(app.Link);
            console.log(app.Link, "linkValues :", linkValues);
            if (linkValues.link) {
                isCurrentPage = setActiveMenu(linkValues.link, linkValues.id, false);
                if (Menu && Menu.length) {
                    (Menu || []).forEach((m: any) => {
                        let isSubMenuActive = false;
                        const subLinkValues = assignLinkValues(m);
                        isSubMenuActive = setActiveMenu(subLinkValues.link, subLinkValues.id, true);

                        // Insert My Details in My Profile
                        if ((Name.includes("MyAccount") || Name.includes("My Account")) && !myDetailsIsInserted) {
                            myDetails && subLinks.unshift({ ...myDetails });
                            myDetailsIsInserted = true;
                        }
                        // Add Security and Share/Delegate to My Details dropdown
                        if (
                            (m.Text.includes("Share/Delegate") || m.Text.includes("Security")) &&
                            (Name.includes("MyAccount") || Name.includes("My Account"))
                        ) {
                            _myProfileSubLinks.push({ ...subLinkValues });
                        }

                        subLinks.push({
                            ...subLinkValues,
                            active: isSubMenuActive,
                        });
                    });

                    // If current page is not coming from the sub-menu
                    // Initialize isCurrentPage to false again
                    if (subLinks.filter((sl) => sl.active).length === 0 && isCurrentPage) {
                        isCurrentPage = false;
                    }
                }
                links.push({
                    ...linkValues,
                    external: Menu.length > 0 ? false : linkValues.external,
                    subLinks,
                    active: isCurrentPage,
                });

                if (isCurrentPage && !hasActive) {
                    hasActive = true;
                    activePage = linkValues.id;
                }
            } else console.error(`${linkValues.label} doesn't have URL value.`);
        }
    });
    // formLinks = _myProfileSubLinks.concat(formLinks);
    formLinks.splice(myDetailsIndx + 1, 0, ..._myProfileSubLinks);
    links.concat(formLinks);

    return {
        links,
        formLinks,
        activePage,
    };
}

export const getNavConfig = async () => {
    const configApi = APP_CONTAINER?.dataset.config || "/Config/Dashboard";
    const apiMethod = APP_CONTAINER?.dataset.configMethod || "post";

    let res: AxiosResponse<any, any>;
    if (apiMethod === "get") {
        res = await axios.get(`${configApi}`);
    } else {
        res = await axios.post(`${configApi}`);
    }
    return res;
};

export const wrapPromise = (promise: Promise<any>) => {
    let status = "pending";
    let result: any;
    let suspender = promise.then(
        (res) => {
            status = "success";
            result = res;
        },
        (err) => {
            status = "error";
            result = err;
        }
    );

    return {
        read() {
            if (status === "pending") {
                throw suspender;
            } else if (status === "error") {
                throw result;
            }
            return result;
        },
    };
};

function assignLinkValues(link: any) {
    const { Icon, Text, Enabled, External, Url, Name } = link;
    return {
        id: Name,
        icon: Icon,
        label: Text,
        link: Url,
        enabled: Enabled,
        external: External,
    };
}

function setActiveMenu(url: string, name: string | null, isSubMenu: boolean) {
    if (name === null) return false;
    try {
        var path = new URL(url).pathname.toLowerCase();
        const { app, appname } = window.Affinity;
        const ID: string = (app || appname || "").toLowerCase();
        const winPathname = window.location.pathname.toLowerCase();
        const currUrl = window.location.href.toLowerCase();

        return (
            (!isSubMenu && (ID === name || ID === name.toLowerCase() || ID.includes(name.toLowerCase()))) ||
            currUrl === url.toLowerCase() ||
            (path !== "/" && path === winPathname) ||
            (path === "/cleverforms/inbox" && winPathname === "/inbox") ||
            (path === "/cleverforms/template" && winPathname === "/template") ||
            (path === "/cleverforms/admin" && winPathname === "/admin")
        );
    } catch (er) {
        return false;
    }
}
